import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { sortIcon } from '../../../assets/images/icons';

import LocationsTable from './LocationsTable';
import SearchBar from '../../../components/SearchBar';
import NewFilterDrawer from '../../../components/shared/FilterDrawer/NewFilterDrawer';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../constants/FilterConstants';
import SORT_CONSTANT, { LOCATIONNAME_ASC } from '../../../constants/SortConstants';

import {
  useLazyGetAllCountriesQuery,
  useLazyGetCountryStatesByCountryIdQuery,
} from '../../../services/CountriesAndStatesAndCities/CountriesAndStatesAndCities';
import {
  useLazyGetAllLocationOwnersQuery,
  useLazyGetAllLocationTypesQuery,
} from '../../../services/Location/LocationService';
import useStyles from './style';
import { useSearchFilterParam } from '../../../hooks';
import {
  useLazyGetProductCategoriesQuery,
  useLazyGetAllSublocationsQuery,
} from '../../../services/Sublocation/SublocationService';
import { useDispatch, useSelector } from 'react-redux';
import { resetActiveLocationFilters, setActiveLocationFilters } from '../../../redux-slice/locationFilters';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/pagination';

const ActiveTab = ({ onTotalLocationsCount }) => {
  const classes = useStyles();
  const { DRAWER_TYPES, TITLE, FILTER_DATA } = FILTER_CONSTANTS;
  const { activeLocation } = useSelector((state) => state.locationFilter);
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [filterParams, setFilterParams] = useState(FILTER_DATA.LOCATION.INITIAL_PARAMS);
  const [sort, setSort] = useState(LOCATIONNAME_ASC);

  useEffect(() => {
    const storedFilterParams = { ...FILTER_DATA.LOCATION.INITIAL_PARAMS };
    storedFilterParams.locationTypeId = activeLocation[FILTER_VALUES.LOCATION_TYPE_ID] ?? [];
    storedFilterParams.ownerId = activeLocation[FILTER_VALUES.OWNER] ?? [];
    storedFilterParams.countryId = activeLocation[FILTER_VALUES.COUNTRY] ?? null;
    storedFilterParams.stateId = activeLocation[FILTER_VALUES.STATE] ?? null;
    storedFilterParams.sublocation = activeLocation[FILTER_VALUES.COLLECTION_SUBLOCATION] ?? [];
    storedFilterParams.productType = activeLocation[FILTER_VALUES.PRODUCT_TYPE] ?? [];
    setSearchText(activeLocation.searchText);
    setSort(activeLocation.sort);
    setFilterParams(storedFilterParams);
  }, [activeLocation]);

  const [getAllLocationTypes] = useLazyGetAllLocationTypesQuery();
  const [getAllLocationOwners] = useLazyGetAllLocationOwnersQuery();
  const [getAllCountries] = useLazyGetAllCountriesQuery();
  const [getStatesByCountryId] = useLazyGetCountryStatesByCountryIdQuery();
  const [getAllSublocations] = useLazyGetAllSublocationsQuery();
  const [getAllProductTypes] = useLazyGetProductCategoriesQuery();

  const getAPI = (value) => {
    switch (value) {
      case FILTER_VALUES.LOCATION_TYPE_ID:
        return getAllLocationTypes;
      case FILTER_VALUES.OWNER:
        return getAllLocationOwners;
      case FILTER_VALUES.COUNTRY:
        return getAllCountries;
      case FILTER_VALUES.STATE:
        return getStatesByCountryId;
      case FILTER_VALUES.COLLECTION_SUBLOCATION:
        return getAllSublocations;
      case FILTER_VALUES.PRODUCT_TYPE:
        return getAllProductTypes;

      default:
        return [];
    }
  };

  const filterData = useMemo(
    () =>
      FILTER_DATA.LOCATION.FILTER.map((item) => {
        const api = getAPI(item.value);

        return {
          ...item,
          api,
        };
      }),
    [],
  );

  const searchParams = useSearchFilterParam({
    filterKey: FILTER_DATA.LOCATION,
    filterParams,
    sort,
    searchText,
    extraParam: 'status=true',
  });

  const searchHandler = (params) => {
    dispatch(
      setActiveLocationFilters({
        ...activeLocation,
        searchText: params,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setSearchText(params);
  };

  const resetSearchBarHandler = () => {
    setSearchText('');
    dispatch(
      setActiveLocationFilters({
        ...activeLocation,
        searchText: '',
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
  };

  const applyFilterHandler = (filterValues) => {
    dispatch(
      setActiveLocationFilters({
        ...activeLocation,
        ...filterValues,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setFilterParams(filterValues);
  };

  const handleSort = (e) => {
    dispatch(
      setActiveLocationFilters({
        ...activeLocation,
        sort: e.target.value,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setSort(e.target.value);
  };

  const resetFilterParams = () => {
    setFilterParams(FILTER_DATA.LOCATION.INITIAL_PARAMS);
    dispatch(resetActiveLocationFilters());
  };

  return (
    <>
      <Box className={classes.searchFilterWrap}>
        <Box className={classes.searchWrap}>
          <SearchBar
            onSearch={searchHandler}
            onReset={resetSearchBarHandler}
            placeholder="Search by location"
            searchText={searchText}
          />
        </Box>
        <Box px={3}>
          <NewFilterDrawer
            name={TITLE.SORT_BY.toUpperCase()}
            title={TITLE.SORT_BY}
            drawerType={DRAWER_TYPES.SORT_DRAWER}
            startIcon={<>{sortIcon}</>}
            onSortSelect={handleSort}
            selectedSort={sort}
            sortData={SORT_CONSTANT.LOCATION_SORT}
          />
        </Box>
        <Box>
          <NewFilterDrawer
            filteredValues={filterParams}
            name={TITLE.FILTERS.toUpperCase()}
            title={TITLE.FILTERS}
            drawerType={DRAWER_TYPES.FILTER_DRAWER}
            onReset={resetFilterParams}
            onApplyFilter={(v) => {
              applyFilterHandler(v);
            }}
            filterData={filterData}
            emptyFilterLength={JSON.stringify(FILTER_DATA.LOCATION.INITIAL_PARAMS).length}
          />
        </Box>
      </Box>
      <LocationsTable
        searchParams={searchParams}
        onTotalLocation={onTotalLocationsCount}
        isArchiveTab={false}
        activeLocation={activeLocation}
      />
    </>
  );
};

export default ActiveTab;
